import React, { useState, useMemo, useRef } from 'react';
import RuleDossier from './RuleDossier';
import './CorpusJurisView.css';
import QuizDetailView from './QuizDetailView'; 
// HistoryModal import removed - we delegate to App.js now

// --- HELPER: recursive HNU finder ---
const getMinHNU = (node) => {
    if (node.sortingHNU) return node.sortingHNU;
    let min = '999999';
    if (node.leafItems && node.leafItems.length > 0) {
        node.leafItems.forEach(item => {
            const itemHnu = item.HNU || item.hnu || '999';
            if (itemHnu.localeCompare(min, undefined, { numeric: true }) < 0) {
                min = itemHnu;
            }
        });
    }
    Object.values(node.childMap || {}).forEach(childNode => {
        const childMin = getMinHNU(childNode);
        if (childMin.localeCompare(min, undefined, { numeric: true }) < 0) {
            min = childMin;
        }
    });
    node.sortingHNU = min;
    return min;
};

const HierarchyNode = ({ node, depth, onInfoClick, onLearnClick, onTestClick, onToggleFlag, headingRefs }) => {
    const combinedChildren = [
        ...(node.subNodes || []), 
        ...(node.leafItems || [])
    ];

    if (combinedChildren.length === 0) return null;

    combinedChildren.sort((a, b) => {
        const hnuA = a.sortingHNU || a.HNU || a.hnu || '999';
        const hnuB = b.sortingHNU || b.HNU || b.hnu || '999';
        const compare = hnuA.localeCompare(hnuB, undefined, { numeric: true });
        if (compare !== 0) return compare;
        const labelA = a.label || a.ruleName || '';
        const labelB = b.label || b.ruleName || '';
        return labelA.localeCompare(labelB);
    });

    const renderChildren = () => {
        return combinedChildren.map((child, idx) => {
            if (child.type === 'heading') {
                return (
                    <HierarchyNode 
                        key={child.label + idx} 
                        node={child} 
                        depth={depth + 1} 
                        {...{ onInfoClick, onLearnClick, onTestClick, onToggleFlag, headingRefs }} 
                    />
                );
            } else {
                return (
                    <div key={child.id} className={`mixed-leaf-wrapper depth-${depth}`}>
                        <RuleDossier 
                            rule={child} 
                            onInfoClick={onInfoClick} 
                            onLearnClick={child.itemType === 'quiz' ? onTestClick : onLearnClick} 
                            onToggleFlag={onToggleFlag} 
                            hnuDepth={child.hnuDepth || 0} 
                        />
                    </div>
                );
            }
        });
    };

    if (depth === 0) return <div className="hierarchy-root">{renderChildren()}</div>;

    if (depth === 1) {
        return (
            <section className="corpus-section major-unit">
                <header 
                    className="major-unit-header"
                    id={`heading-${node.label.replace(/\s+/g, '-')}`}
                    ref={el => headingRefs.current.set(node.label, el)}
                >
                    <span className="unit-label">{node.label}</span>
                    <div className="header-decoration"></div>
                </header>
                <div className="major-unit-content">
                    {renderChildren()}
                </div>
            </section>
        );
    }

    if (depth === 2) {
        return (
            <div className="corpus-subsection-card">
                <h3 className="subsection-header">{node.label}</h3>
                <div className="subsection-content">
                    {renderChildren()}
                </div>
            </div>
        );
    }

    return (
        <div className="deep-nested-group">
            <h4 className="deep-group-header">{node.label}</h4>
            <div className="deep-group-content">
                {renderChildren()}
            </div>
        </div>
    );
};

const CorpusJurisView = ({ subject, setSubject, subjectMapDerived, allSubjectsData, flaggedForReviewCount, onOpenHistoryModal: parentOpenHistoryModal, onLearnClick, onRefineClick, onTestClick, onToggleFlag, onStartChapterLearn, onStartChapterRefine, onStartChapterFlagged, userAccessLevel, setView, onUpdateRule }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [activeHeading, setActiveHeading] = useState(null);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    
    // State for local QUIZ modal only (Rules are delegated to App.js)
    const [modalContent, setModalContent] = useState(null); 
    const [modalItem, setModalItem] = useState(null);

    const headingRefs = useRef(new Map());
    const items = useMemo(() => allSubjectsData[subject] || [], [allSubjectsData, subject]);

    const filteredItems = useMemo(() => {
        return items.filter(item => {
            if (userAccessLevel === 'free' && !item.is_free_preview) return false;
            const matchesSearch = item.ruleName.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === 'all' || item.status === statusFilter || (statusFilter === 'quizzes_only' && item.itemType === 'quiz') || (statusFilter === 'quizzes' && item.itemType === 'quiz');
            return matchesSearch && matchesStatus;
        });
    }, [items, searchTerm, statusFilter, userAccessLevel]);

    const hierarchy = useMemo(() => {
        const root = { label: 'root', type: 'root', subNodes: [], leafItems: [], childMap: {} };
        filteredItems.forEach(item => {
            let current = root;
            const path = item.displayPath || ['General'];
            path.forEach((segment) => {
                if (!current.childMap[segment]) {
                    const newNode = { label: segment, type: 'heading', subNodes: [], leafItems: [], childMap: {} };
                    current.childMap[segment] = newNode;
                    current.subNodes.push(newNode);
                }
                current = current.childMap[segment];
            });
            current.leafItems.push(item);
        });
        getMinHNU(root);
        return root;
    }, [filteredItems]);

    const headings = useMemo(() => {
        return (hierarchy.subNodes || [])
            .sort((a, b) => (a.sortingHNU || '999').localeCompare(b.sortingHNU || '999', undefined, { numeric: true }))
            .map(h => h.label);
    }, [hierarchy]);

    const handleSidebarClick = (headingLabel) => {
        const el = headingRefs.current.get(headingLabel);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setActiveHeading(headingLabel);
            window.history.replaceState(null, '', `#heading-${headingLabel.replace(/\s+/g, '-')}`);
        }
    };

    // FIXED: Delegate Rule opening to parent (App.js) to ensure tracking
    const handleOpenModal = (item) => {
        if (!item) return;
        if (item.itemType === 'quiz') {
            setModalContent('quiz');
            setModalItem(item);
        } else { 
            // Delegate to App.js -> triggers handleOpenHistoryModal -> triggers Viewed Rules Tracking
            if (parentOpenHistoryModal) {
                parentOpenHistoryModal(item.id);
            }
        }
    };
    
    const handleCloseLocalModal = () => {
        setModalContent(null);
        setModalItem(null);
    };

    return (
        <main className="corpus-juris theme-aware">
            <header className="corpus-header">
                <div style={{display:'flex', flexDirection:'column', gap:'5px'}}>
                    <button className="text-link" onClick={() => setView('library')}>← Back to Map</button>
                </div>
            </header>

            <div className="chapter-actions-container">
                <h2 id="corpus-subject-title">{subjectMapDerived[subject]}</h2>
                <button className="btn chapter-btn learn" onClick={onStartChapterLearn}>
                    <span className="material-symbols-outlined">school</span> Learn Chapter
                </button>
                <button className="btn chapter-btn refine" onClick={onStartChapterRefine}>
                    <span className="material-symbols-outlined">science</span> Refine Chapter
                </button>
                <button className="btn chapter-btn flagged" onClick={onStartChapterFlagged}>
                    <span className="material-symbols-outlined">flag</span> Flagged ({flaggedForReviewCount})
                </button>
            </div>

            <div className="corpus-layout-container">
                {isMobileSidebarOpen && <div id="mobile-sidebar-overlay" onClick={() => setIsMobileSidebarOpen(false)} />}
                
                <aside className={`corpus-sidebar ${isMobileSidebarOpen ? 'mobile-open' : ''}`}>
                    <div className="sidebar-section">
                        <input type="search" className="search-bar" placeholder="Filter rules..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        <div className="status-filter-group theme-aware">
                            <button onClick={() => setStatusFilter('all')} className={statusFilter === 'all' ? 'active' : ''}>All</button>
                            <button onClick={() => setStatusFilter('not_started')} className={statusFilter === 'not_started' ? 'active' : ''}>New</button>
                            <button onClick={() => setStatusFilter('in_progress')} className={statusFilter === 'in_progress' ? 'active' : ''}>Refine</button>
                            <button onClick={() => setStatusFilter('mastered')} className={statusFilter === 'mastered' ? 'active' : ''}>Mastered</button>
                            <button onClick={() => setStatusFilter('quizzes_only')} className={statusFilter === 'quizzes_only' ? 'active' : ''}>Quizzes</button>
                        </div>
                        <nav className="sidebar-section scrollable-nav">
                            <h4 className="sidebar-toc-header">Table of Contents</h4>
                            <ul>{headings.map(h => (
                                <li key={h}>
                                    <a href="#" className={activeHeading === h ? 'active' : ''} onClick={(e) => { e.preventDefault(); handleSidebarClick(h); setIsMobileSidebarOpen(false); }}>
                                        {h}
                                    </a>
                                </li>
                            ))}</ul>
                        </nav>
                    </div>
                </aside>

                <main className="corpus-main-content">
                    <HierarchyNode node={hierarchy} depth={0} onInfoClick={handleOpenModal} onLearnClick={onLearnClick} onTestClick={onTestClick} onToggleFlag={onToggleFlag} headingRefs={headingRefs} />
                </main>
            </div>

            {/* Only Render Quiz Details Locally. Rules are handled by App.js global modal. */}
            {modalContent === 'quiz' && modalItem && (
                <QuizDetailView 
                    quiz={modalItem} 
                    onClose={handleCloseLocalModal} 
                    allRulesMap={new Map(allSubjectsData ? Object.entries(allSubjectsData).flatMap(([subj, rules]) => rules.map(r => [r.id, r])) : [])} 
                />
            )}
        </main>
    );
};

export default CorpusJurisView;